import { Box, Button, Typography } from '@mui/material';
import type { PlanStudent } from './derive';
import { hasLiveValue, type StudentPlanData } from './planData';
import { gradeText, metaItems, schoolName } from './headerData';
import type { PlanStanding } from './planRecordApi';
import { COLORS } from './tokens';

export type AltHeaderBadge = { label: string; variant: 'solid' | 'grey' };
export type AltHeaderField = { label: string; value: string };
export type AltHeaderData = {
  fullName: string;
  badges: AltHeaderBadge[];
  subline: string;
  fields: AltHeaderField[];
};

const FIELD_LABELS: Record<string, string> = {
  'Home Language': 'Home language',
  'Years in EL': 'Years in EL services',
  Status: 'Status',
  'Program Model': 'Program model',
  'EL Teacher': 'Assigned EL teacher',
};

export function buildAltHeader(
  student: PlanStudent | null,
  live: StudentPlanData | null,
  standing: PlanStanding | null = null,
): AltHeaderData {
  const badges: AltHeaderBadge[] = [{ label: 'Alternate ACCESS', variant: 'solid' }];
  if (student?.sis?.duallyIdentified) badges.push({ label: 'IEP', variant: 'grey' });
  const district =
    student?.district || (hasLiveValue(live?.district.name) ? live.district.name : '');
  const districtLabel = district
    ? student?.districtState
      ? `${district} (${student.districtState})`
      : district
    : '';
  const rawGrade = gradeText(student, live).replace(/^Grade\s+/, '').trim();
  const grade = /^(k|kg|kindergarten)$/i.test(rawGrade) || /^0+$/.test(rawGrade)
    ? 'Grade K'
    : /^\d+$/.test(rawGrade) ? `Grade ${parseInt(rawGrade, 10)}` : gradeText(student, live);
  const subline = [grade, schoolName(student, live), districtLabel]
    .filter((part) => part !== '')
    .join(' · ');
  const fields = metaItems(student, live, undefined, standing).map(({ label, value }) => ({
    label: FIELD_LABELS[label] ?? label,
    value: label === 'Years in EL' ? value.replace(/ · (.+)$/, ' ($1)') : value,
  }));
  const liveName =
    hasLiveValue(live?.student.firstName) && hasLiveValue(live?.student.lastName)
      ? `${live.student.firstName} ${live.student.lastName}`
      : '';
  return { fullName: student?.fullName || liveName || 'Student', badges, subline, fields };
}

export function AltHeader({
  data,
  generatedDate,
}: {
  data: AltHeaderData;
  generatedDate: string;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              color: COLORS.muted,
            }}
          >
            Student Action Plan
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', mt: '6px' }}>
            <Typography sx={{ fontSize: 28, lineHeight: '36px', fontWeight: 600, color: COLORS.ink }}>
              {data.fullName}
            </Typography>
            {data.badges.map((badge) => (
              <Box
                key={badge.label}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '3px 10px',
                  borderRadius: '9999px',
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: badge.variant === 'solid' ? COLORS.navy : COLORS.bg,
                  color: badge.variant === 'solid' ? '#FFFFFF' : '#4B4B63',
                }}
              >
                {badge.label}
              </Box>
            ))}
          </Box>
          {data.subline !== '' && (
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: COLORS.muted, mt: '6px' }}>
              {data.subline}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
            {`Generated ${generatedDate}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              sx={{
                textTransform: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                border: `1px solid ${COLORS.navy}`,
                color: COLORS.navy,
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              Print
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={{ height: '1px', bgcolor: COLORS.divider }} />
      <Box data-testid="alt-header-details" sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {data.fields
          .filter((field) => field.value.trim() !== '')
          .map((field) => (
            <Box key={field.label}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: COLORS.muted,
                }}
              >
                {field.label}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 600, color: COLORS.ink, mt: '2px' }}>
                {field.value}
              </Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
